/**
 * A USB radio carrying several aircraft, shared from this laptop's browser.
 *
 * The one-drone USB bridge maps the port to a single drone, which is exactly
 * the assumption that loses every aircraft but the first on a meshed RFD900x.
 * This sends the same bytes to `/ws/mesh-bridge` instead, and the server splits
 * them by system id into one drone per aircraft.
 *
 * The browser is the only place two links of the chain can be seen. It frames
 * the bytes before sending, so a radio at the wrong baud rate sends the server
 * nothing at all; only this side can say "bytes are arriving and none of them
 * decode". The server reports the rest of the chain.
 */

import {
  MavlinkFramer,
  frameHeader,
  openSerialMavlink,
  type SerialSession,
} from './webSerialBridge';
import { deviceTag, openBridgeSocket, type BridgeSocket } from './serialBridgeClient';
import type { ChainStage } from '@/api/mesh';

const MAVLINK_MSG_ID_HEARTBEAT = 0;
const MAV_COMP_ID_AUTOPILOT1 = 1;

/** Mirrors CLOSE_MESH_REFUSED in api/app/routers/serial_bridge.py. */
export const CLOSE_MESH_REFUSED = 4009;

export interface MeshProbeStatus {
  portOpen: boolean;
  bytes: number;
  frames: number;
  /** System ids heard heartbeating from an autopilot, ascending. */
  sysIds: number[];
}

/**
 * What the port is producing, aircraft by aircraft.
 *
 * Only an autopilot's heartbeat counts as an aircraft. A second ground station
 * on the mesh, or an ADSB receiver, heartbeats too, and listing those as
 * aircraft would promise drones the server will never make.
 */
export class MeshProbe {
  private framer = new MavlinkFramer();
  private bytes = 0;
  private frames = 0;
  private heard = new Set<number>();

  push(chunk: Uint8Array): Uint8Array[] {
    this.bytes += chunk.length;
    const out: Uint8Array[] = [];
    for (const frame of this.framer.push(chunk)) {
      this.frames += 1;
      const header = frameHeader(frame.bytes);
      if (
        header !== null &&
        header.msgId === MAVLINK_MSG_ID_HEARTBEAT &&
        header.compId === MAV_COMP_ID_AUTOPILOT1
      ) {
        this.heard.add(header.sysId);
      }
      out.push(frame.bytes);
    }
    return out;
  }

  status(portOpen: boolean): MeshProbeStatus {
    return {
      portOpen,
      bytes: this.bytes,
      frames: this.frames,
      sysIds: [...this.heard].sort((a, b) => a - b),
    };
  }
}

/**
 * The browser's half of the chain: the port, the bytes, the decoding. Each
 * stage is only healthy when every one before it is.
 */
export function browserChain(status: MeshProbeStatus): ChainStage[] {
  const arriving = status.portOpen && status.bytes > 0;
  const decoding = arriving && status.frames > 0;
  return [
    {
      key: 'port',
      ok: status.portOpen,
      detail: status.portOpen ? 'Serial port open' : 'The serial port is not open.',
    },
    {
      key: 'bytes',
      ok: arriving,
      detail: arriving
        ? `${status.bytes} bytes from the radio`
        : 'No bytes yet. Is the radio powered, and is this the right port?',
    },
    {
      key: 'frames',
      ok: decoding,
      detail: decoding
        ? `${status.frames} MAVLink frames`
        : arriving
          ? 'Bytes are arriving but none decode as MAVLink. Check the baud rate.'
          : 'Nothing to decode yet.',
    },
  ];
}

/**
 * The whole chain for a browser-shared mesh: this side's stages for the port,
 * bytes and frames, the server's for heartbeats and the return path.
 */
export function mergeChain(browser: ChainStage[], server: ChainStage[]): ChainStage[] {
  const local = new Set(browser.map((stage) => stage.key));
  const merged = [...browser, ...server.filter((stage) => !local.has(stage.key))];
  let broken = false;
  return merged.map((stage) => {
    const ok = stage.ok && !broken;
    if (!stage.ok) broken = true;
    return { ...stage, ok };
  });
}

export function refusalMessage(code: number, reason: string): string {
  if (code === CLOSE_MESH_REFUSED && reason !== '') return reason;
  if (reason !== '') return reason;
  return 'The server closed the mesh.';
}

export interface MeshBridgeState {
  running: boolean;
  meshId: string;
  label: string;
  baudRate: number;
  status: MeshProbeStatus;
  error: string | null;
}

const IDLE_STATUS: MeshProbeStatus = { portOpen: false, bytes: 0, frames: 0, sysIds: [] };
const IDLE: MeshBridgeState = {
  running: false,
  meshId: '',
  label: '',
  baudRate: 57600,
  status: IDLE_STATUS,
  error: null,
};

let state: MeshBridgeState = IDLE;
const listeners = new Set<() => void>();
let running: { session: SerialSession; socket: BridgeSocket } | null = null;

function publish(next: Partial<MeshBridgeState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function subscribeMeshBridge(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function meshBridgeState(): MeshBridgeState {
  return state;
}

interface Deps {
  openPort: (baudRate: number) => Promise<SerialSession>;
  openSocket: (meshId: string, device: string, baudRate: number) => Promise<BridgeSocket>;
  now: () => number;
}

const defaults: Deps = {
  openPort: openSerialMavlink,
  openSocket: (meshId, device, baudRate) =>
    openBridgeSocket(
      '/ws/mesh-bridge',
      new URLSearchParams({ meshId, device, baud: String(baudRate), origin: 'browser' }),
      'The server refused to share this radio. Sharing one needs operator access.',
    ),
  now: () => Date.now(),
};

const STATUS_INTERVAL_MS = 200;

export async function startMeshBridge(
  params: { meshId: string; baudRate: number },
  overrides: Partial<Deps> = {},
): Promise<void> {
  const deps = { ...defaults, ...overrides };
  if (running !== null) throw new Error('A mesh radio is already shared from this tab.');

  publish({ ...IDLE, baudRate: params.baudRate });
  const session = await deps.openPort(params.baudRate);
  let socket: BridgeSocket;
  try {
    socket = await deps.openSocket(params.meshId, deviceTag(session.label), params.baudRate);
  } catch (error) {
    await session.close();
    throw error;
  }

  running = { session, socket };
  const probe = new MeshProbe();
  publish({
    running: true,
    meshId: params.meshId,
    label: session.label,
    baudRate: params.baudRate,
    status: probe.status(true),
    error: null,
  });

  socket.onBytes((bytes) => {
    void session.write(bytes).catch(() => {
      // The port went away mid-write. The read loop owns the teardown.
    });
  });
  socket.onClosed((code, reason) => {
    void stopMeshBridge(refusalMessage(code, reason));
  });

  void (async () => {
    let lastPublish = 0;
    try {
      for await (const chunk of session.chunks) {
        for (const frame of probe.push(chunk)) socket.send(frame);
        const moment = deps.now();
        if (moment - lastPublish >= STATUS_INTERVAL_MS) {
          lastPublish = moment;
          publish({ status: probe.status(true) });
        }
      }
    } catch (error) {
      publish({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      // A port that stops reading is a radio that was unplugged. Ending the
      // share takes its drones out of the fleet rather than leaving aircraft
      // on the map with nothing behind them.
      if (running !== null && running.session === session) await stopMeshBridge();
    }
  })();
}

export async function stopMeshBridge(reason: string | null = null): Promise<void> {
  const current = running;
  running = null;
  if (current === null) {
    if (reason !== null) publish({ running: false, error: reason });
    return;
  }
  current.socket.close();
  await current.session.close().catch(() => {});
  publish({ running: false, status: IDLE_STATUS, error: reason });
}

export const __testing = {
  reset: (): void => {
    running = null;
    state = IDLE;
    listeners.clear();
  },
};
